import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const generatePresencePDF = async (presenceData, type = 'individuelle') => {
  const { stagiaire, formateur, tuteur, date, statut, signature_stagiaire, signature_formateur, centre } = presenceData;
  
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // En-tête FISL
  pdf.setFillColor(141, 198, 63); // #8DC63F
  pdf.rect(0, 0, pageWidth, 30, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.text('FISL', 20, 20);
  
  pdf.setFontSize(10);
  pdf.text('Formation Internationale aux Sports de Loisirs', 60, 15);
  pdf.text('BPJEPS Activités de Motonautisme et Disciplines Associées', 60, 22);
  
  // Titre
  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(18);
  pdf.text(`Feuille de Présence ${type === 'individuelle' ? 'Individuelle' : 'Collective'}`, 20, 45);
  
  // Date et Centre
  pdf.setFontSize(12);
  pdf.text(`Date: ${new Date(date).toLocaleDateString('fr-FR')}`, 20, 60);
  pdf.text(`Centre: ${centre || 'Atlantique'}`, 120, 60);
  
  let yPos = 75;
  
  if (type === 'individuelle') {
    // Informations Stagiaire
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Stagiaire', 20, yPos);
    pdf.setFont(undefined, 'normal');
    yPos += 10;
    
    pdf.setFontSize(11);
    pdf.text(`Nom: ${stagiaire?.nom || ''} ${stagiaire?.prenom || ''}`, 20, yPos);
    yPos += 7;
    pdf.text(`Statut: ${stagiaire?.statut || 'Stagiaire'}`, 20, yPos);
    yPos += 15;
    
    // Statut de présence
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(14);
    pdf.text('Statut de Présence', 20, yPos);
    yPos += 10;
    
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(12);
    
    const statutColors = {
      'present': [141, 198, 63],
      'absent': [239, 68, 68],
      'retard': [245, 158, 11],
      'conges': [59, 130, 246]
    };
    
    const color = statutColors[statut] || [100, 100, 100];
    pdf.setFillColor(...color);
    pdf.rect(20, yPos - 5, 60, 10, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.text(statut.toUpperCase(), 25, yPos + 2);
    pdf.setTextColor(15, 23, 42);
    
    yPos += 20;
    
    // Formateur ou Tuteur
    if (formateur || tuteur) {
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(14);
      pdf.text(formateur ? 'Formateur' : 'Tuteur', 20, yPos);
      pdf.setFont(undefined, 'normal');
      yPos += 10;
      
      pdf.setFontSize(11);
      const responsable = formateur || tuteur;
      pdf.text(`Nom: ${responsable?.nom || ''} ${responsable?.prenom || ''}`, 20, yPos);
      yPos += 15;
    }
    
    // Signatures
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(14);
    pdf.text('Signatures', 20, yPos);
    yPos += 10;
    pdf.setFont(undefined, 'normal');
    
    // Signature Stagiaire
    if (signature_stagiaire) {
      pdf.setFontSize(10);
      pdf.text('Signature Stagiaire:', 20, yPos);
      try {
        pdf.addImage(signature_stagiaire, 'PNG', 20, yPos + 5, 60, 20);
      } catch (e) {
      }
    }
    
    // Signature Formateur/Tuteur
    if (signature_formateur) {
      pdf.setFontSize(10);
      pdf.text(formateur ? 'Signature Formateur:' : 'Signature Tuteur:', 110, yPos);
      try {
        pdf.addImage(signature_formateur, 'PNG', 110, yPos + 5, 60, 20);
      } catch (e) {
      }
    }
  }
  
  // Pied de page
  pdf.setFontSize(8);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 20, pageHeight - 10);
  pdf.text('FISL - Formation Motonautisme', pageWidth - 60, pageHeight - 10);
  
  return pdf;
};

export const generatePresenceCollectivePDF = async (presencesData) => {
  const { date, centre, periode, formateurs = [], stagiaires = [] } = presencesData;
  
  const isJournee = periode === 'journee';
  const isMatin = periode === 'matin';
  const isApresMidi = periode === 'apres_midi';
  
  const pdf = new jsPDF({ orientation: isJournee ? 'landscape' : 'portrait' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  const VERT = [141, 198, 63];
  const GRIS = [15, 23, 42];
  const BLANC = [255, 255, 255];
  const GRIS_CLAIR = [241, 245, 249];
  const GRIS_TEXTE = [100, 116, 139];
  
  // === Charger le logo ===
  let logoImg = null;
  try {
    const logoResponse = await fetch('/logo-fisl-transparent.png');
    const blob = await logoResponse.blob();
    logoImg = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) { console.log('Logo non disponible'); }
  
  const periodeLabel = isJournee ? 'Journée complète (Matin + Après-midi)' : isMatin ? 'Matin' : 'Après-midi';
  const dateFormatted = new Date(date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  
  // === EN-TÊTE ===
  const drawHeader = (y0) => {
    pdf.setFillColor(...VERT);
    pdf.rect(0, y0, pageWidth, 32, 'F');
    
    let logoX = 12;
    if (logoImg) {
      try { pdf.addImage(logoImg, 'PNG', logoX, y0 + 3, 26, 26); logoX = 42; } catch { logoX = 12; }
    }
    
    pdf.setTextColor(...BLANC);
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    pdf.text('FISL', logoX, y0 + 14);
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.text('Formation Internationale aux Sports de Loisirs', logoX, y0 + 21);
    pdf.text('BPJEPS Activités de Motonautisme et Disciplines Associées', logoX, y0 + 27);
    
    // Période en haut à droite
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    const periodeBadge = periodeLabel;
    const badgeW = pdf.getStringUnitWidth(periodeBadge) * 11 / pdf.internal.scaleFactor + 10;
    pdf.setFillColor(255, 255, 255, 0.2);
    pdf.roundedRect(pageWidth - badgeW - 12, y0 + 8, badgeW, 14, 3, 3, 'F');
    pdf.setTextColor(...BLANC);
    pdf.text(periodeBadge, pageWidth - 12, y0 + 18, { align: 'right' });
    
    return y0 + 32;
  };
  
  let y = drawHeader(0);
  y += 8;
  
  // === Sous-en-tête : Date + Centre ===
  pdf.setTextColor(...GRIS);
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.text('FEUILLE DE PRÉSENCE COLLECTIVE', pageWidth / 2, y, { align: 'center' });
  y += 8;
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(...GRIS_TEXTE);
  pdf.text(`Date : ${dateFormatted}`, 15, y);
  pdf.text(`Centre : ${centre}`, pageWidth - 15, y, { align: 'right' });
  y += 6;
  
  if (formateurs.length > 0) {
    const fNames = formateurs.map(f => `${f.prenom} ${f.nom}`).join(', ');
    const fLabel = formateurs.length > 1 ? 'Formateurs' : (formateurs[0]?.role === 'tuteur' ? 'Tuteur' : 'Formateur');
    pdf.text(`${fLabel} : ${fNames}`, 15, y);
    y += 6;
  }
  
  y += 4;
  
  // === TABLEAU ===
  const marginX = 12;
  const tableW = pageWidth - marginX * 2;
  
  // Dimensions colonnes
  let cols;
  if (isJournee) {
    // Landscape: Nom | Matin | Sig Matin | Après-midi | Sig Après-midi
    const colNom = 60;
    const colStatut = 28;
    const colSig = (tableW - colNom - colStatut * 2) / 2;
    cols = [
      { label: 'Stagiaire', x: marginX, w: colNom },
      { label: 'Matin', x: marginX + colNom, w: colStatut },
      { label: 'Signature Matin', x: marginX + colNom + colStatut, w: colSig },
      { label: 'Après-midi', x: marginX + colNom + colStatut + colSig, w: colStatut },
      { label: 'Signature A-M', x: marginX + colNom + colStatut * 2 + colSig, w: colSig }
    ];
  } else {
    // Portrait: Nom | Statut | Signature
    const colNom = 55;
    const colStatut = 30;
    const colSig = tableW - colNom - colStatut;
    cols = [
      { label: 'Stagiaire', x: marginX, w: colNom },
      { label: 'Statut', x: marginX + colNom, w: colStatut },
      { label: 'Signature', x: marginX + colNom + colStatut, w: colSig }
    ];
  }
  
  const rowHeight = 14;
  const headerH = 8;
  
  const drawTableHeader = () => {
    pdf.setFillColor(...VERT);
    pdf.rect(marginX, y, tableW, headerH, 'F');
    pdf.setTextColor(...BLANC);
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'bold');
    cols.forEach(col => {
      pdf.text(col.label, col.x + 3, y + 5.5);
    });
    y += headerH;
  };
  
  drawTableHeader();
  
  const statutLabels = {
    present: 'Présent', absent: 'Absent', retard: 'Retard',
    absent_justifie: 'Abs. justifiée', justifie: 'Abs. justifiée',
    conge: 'Congé', conges: 'Congé'
  };
  const statutColors = {
    present: [34, 197, 94], absent: [239, 68, 68], retard: [245, 158, 11],
    absent_justifie: [234, 88, 12], justifie: [234, 88, 12],
    conge: [99, 102, 241], conges: [99, 102, 241]
  };
  
  // Trier par nom
  const sortedStagiaires = [...stagiaires].sort((a, b) => {
    const nameA = `${a.nom} ${a.prenom}`.toLowerCase();
    const nameB = `${b.nom} ${b.prenom}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });
  
  const drawStatutBadge = (statut, x, yRow) => {
    if (!statut) { pdf.setTextColor(...GRIS_TEXTE); pdf.setFontSize(7); pdf.text('-', x + 3, yRow); return; }
    const color = statutColors[statut] || [100, 100, 100];
    const label = statutLabels[statut] || statut;
    pdf.setFillColor(...color);
    const bW = Math.min(26, pdf.getStringUnitWidth(label) * 7 / pdf.internal.scaleFactor + 6);
    pdf.roundedRect(x + 1, yRow - 3.5, bW, 5, 1, 1, 'F');
    pdf.setTextColor(...BLANC);
    pdf.setFontSize(6.5);
    pdf.setFont(undefined, 'bold');
    pdf.text(label, x + 3, yRow);
    pdf.setFont(undefined, 'normal');
  };
  
  const drawSignature = (sigData, x, yRow, maxW, maxH) => {
    if (!sigData) return;
    try {
      pdf.addImage(sigData, 'PNG', x + 2, yRow - maxH + 2, maxW - 4, maxH - 2);
    } catch {
      pdf.setTextColor(...GRIS_TEXTE);
      pdf.setFontSize(6);
      pdf.text('(signée)', x + 3, yRow);
    }
  };
  
  // Stats
  const stats = { present: 0, absent: 0, retard: 0, justifie: 0, conge: 0 };
  
  sortedStagiaires.forEach((stag, index) => {
    // Nouvelle page si nécessaire
    if (y + rowHeight > pageHeight - 50) {
      // Footer
      pdf.setFontSize(6);
      pdf.setTextColor(...GRIS_TEXTE);
      pdf.text(`FISL - ${dateFormatted} - ${periodeLabel}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
      pdf.addPage();
      y = 10;
      drawTableHeader();
    }
    
    // Fond alterné
    if (index % 2 === 0) {
      pdf.setFillColor(...GRIS_CLAIR);
      pdf.rect(marginX, y, tableW, rowHeight, 'F');
    }
    
    // Ligne de séparation
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(marginX, y + rowHeight, marginX + tableW, y + rowHeight);
    
    const textY = y + rowHeight / 2 + 1;
    const sigMaxH = rowHeight - 2;
    
    // Nom
    pdf.setTextColor(...GRIS);
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    const fullName = `${stag.prenom} ${stag.nom}`.substring(0, 30);
    pdf.text(fullName, cols[0].x + 3, textY);
    
    if (isJournee) {
      // Matin statut
      const matinStatut = stag.matin?.statut;
      drawStatutBadge(matinStatut, cols[1].x, textY);
      // Matin signature
      if (stag.matin?.signature && matinStatut && ['present', 'retard'].includes(matinStatut)) {
        drawSignature(stag.matin.signature, cols[2].x, y + rowHeight, cols[2].w, sigMaxH);
      }
      // Après-midi statut
      const apmStatut = stag.apres_midi?.statut;
      drawStatutBadge(apmStatut, cols[3].x, textY);
      // Après-midi signature
      if (stag.apres_midi?.signature && apmStatut && ['present', 'retard'].includes(apmStatut)) {
        drawSignature(stag.apres_midi.signature, cols[4].x, y + rowHeight, cols[4].w, sigMaxH);
      }
      // Stats
      [matinStatut, apmStatut].forEach(s => {
        if (s === 'present') stats.present++;
        else if (s === 'absent') stats.absent++;
        else if (s === 'retard') stats.retard++;
        else if (s === 'justifie' || s === 'absent_justifie') stats.justifie++;
        else if (s === 'conge' || s === 'conges') stats.conge++;
      });
    } else {
      // Mono-période
      const period = isMatin ? stag.matin : stag.apres_midi;
      const statut = period?.statut;
      drawStatutBadge(statut, cols[1].x, textY);
      if (period?.signature && statut && ['present', 'retard'].includes(statut)) {
        drawSignature(period.signature, cols[2].x, y + rowHeight, cols[2].w, sigMaxH);
      }
      if (statut === 'present') stats.present++;
      else if (statut === 'absent') stats.absent++;
      else if (statut === 'retard') stats.retard++;
      else if (statut === 'justifie' || statut === 'absent_justifie') stats.justifie++;
      else if (statut === 'conge' || statut === 'conges') stats.conge++;
    }
    
    y += rowHeight;
  });
  
  // === STATISTIQUES ===
  y += 8;
  if (y > pageHeight - 65) { pdf.addPage(); y = 15; }
  
  pdf.setFillColor(...GRIS_CLAIR);
  pdf.roundedRect(marginX, y, tableW, 14, 2, 2, 'F');
  y += 4;
  pdf.setFontSize(8);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(...GRIS);
  pdf.text('Résumé :', marginX + 4, y + 4);
  
  const statsItems = [
    [`Présents: ${stats.present}`, [34, 197, 94]],
    [`Retards: ${stats.retard}`, [245, 158, 11]],
    [`Absents: ${stats.absent}`, [239, 68, 68]],
    [`Justifiés: ${stats.justifie}`, [234, 88, 12]],
    [`Congés: ${stats.conge}`, [99, 102, 241]]
  ];
  let sx = marginX + 28;
  statsItems.forEach(([label, color]) => {
    pdf.setTextColor(...color);
    pdf.setFont(undefined, 'bold');
    pdf.text(label, sx, y + 4);
    sx += pdf.getStringUnitWidth(label) * 8 / pdf.internal.scaleFactor + 8;
  });
  y += 16;
  
  // === SIGNATURES FORMATEURS ===
  if (y > pageHeight - 50) { pdf.addPage(); y = 15; }
  
  pdf.setDrawColor(...VERT);
  pdf.setLineWidth(0.8);
  pdf.line(marginX, y, marginX + tableW, y);
  y += 6;
  
  if (formateurs.length > 0) {
    const sigBoxW = Math.min(70, (tableW - 10) / formateurs.length);
    formateurs.forEach((f, i) => {
      const fx = marginX + i * (sigBoxW + 10);
      const roleLabel = f.role === 'tuteur' ? 'Tuteur' : 'Formateur';
      pdf.setTextColor(...GRIS);
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'bold');
      pdf.text(`${roleLabel} : ${f.prenom} ${f.nom}`, fx, y);
      
      if (f.signature) {
        try {
          pdf.addImage(f.signature, 'PNG', fx, y + 2, sigBoxW - 5, 20);
        } catch {
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(...GRIS_TEXTE);
          pdf.text('(Signature enregistrée)', fx, y + 12);
        }
      } else {
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(fx, y + 2, sigBoxW - 5, 20);
        pdf.setFontSize(7);
        pdf.setTextColor(...GRIS_TEXTE);
        pdf.setFont(undefined, 'normal');
        pdf.text('Signature manquante', fx + 5, y + 14);
      }
    });
    y += 30;
  }
  
  // === PIED DE PAGE ===
  pdf.setFontSize(6);
  pdf.setTextColor(...GRIS_TEXTE);
  pdf.text(
    `FISL - Formation Internationale aux Sports de Loisirs - Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')} - Document confidentiel`,
    pageWidth / 2, pageHeight - 5, { align: 'center' }
  );
  
  return pdf;
};

export const downloadPDF = (pdf, filename) => {
  pdf.save(filename);
};
